'use client';

/* eslint-disable react-hooks/rules-of-hooks */
import React, { useEffect, useState } from 'react';
import { Avatar, Dropdown, Element, Rule, Spinner } from '@/components/core';
import { msg } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useUser } from '@/components/context/user';
import { useLingui } from '@lingui/react';
import CollectionLogo from '@/components/core/components/molecules/collection-logo';
import clsx from 'clsx';
import { usePathname } from 'next/navigation';
import InternalLink from '@/components/core/components/organism/internal-link';
import { CollectionType, ProductType } from '@/@types';
import { useLocalizedLinks } from '@/components/core/hooks';
import SearchInput from '@/components/core/components/molecules/search-input';
import { categories } from '@/@constants';
import * as styles from './search-bar.styles';

const hasResults = (searchResults: any) => {
  return (
    searchResults &&
    (searchResults.topResult.length > 0 ||
      searchResults.collections.length > 0 ||
      searchResults.products.length > 0 ||
      searchResults.users.length > 0)
  );
};

const ItemTypeMapper = {
  user: ({ data }: any) => {
    const links = useLocalizedLinks();
    return (
      <Dropdown.Item as={InternalLink} href={links.profile(data).root} className={styles.item}>
        <Avatar size={48} src={data.avatar} level={data.level?.name} />
        <div>
          <Element lines={1} className={styles.itemTitle}>
            {data.nickname}
          </Element>
        </div>
      </Dropdown.Item>
    );
  },
  product: ({ data }: { data: ProductType }) => {
    const { i18n } = useLingui();
    const links = useLocalizedLinks();
    const category = categories[data.category.name];
    return (
      <Dropdown.Item as={InternalLink} href={links.product(data)} className={styles.item}>
        <Avatar size={48} src={data.image} />
        <div>
          <Element lines={1} className={styles.itemTitle}>
            {data.name}
          </Element>
          <Element lines={1} className={styles.itemSubtitle}>
            {i18n._(category.itemTitle)}
          </Element>
        </div>
      </Dropdown.Item>
    );
  },
  collection: ({ data }: { data: CollectionType }) => {
    const links = useLocalizedLinks();
    return (
      <Dropdown.Item as={InternalLink} href={links.collection(data)} className={styles.item}>
        <CollectionLogo collection={data} size={48} />
        <div>
          <Element lines={1} className={styles.itemTitle}>
            {data.name}
          </Element>
          <Element lines={1} className={styles.itemSubtitle}>
            {data.owner?.nickname}
          </Element>
        </div>
      </Dropdown.Item>
    );
  },
};

const SearchItem = ({ type, data }: { type: keyof typeof ItemTypeMapper; data: any }) => {
  const Component = ItemTypeMapper[type] as React.FC<{ data: any }>;
  if (!Component) {
    return null;
  }
  return <Component data={data} />;
};

const Section: React.FC<{ items: any[]; title: React.ReactNode }> = ({ items, title }) => {
  return (
    items.length > 0 && (
      <>
        <div className={styles.sectionTitle}>
          <div className="flex justify-between w-full">
            <span className="text-nowrap">{title}</span>
          </div>
          <Rule className="w-full" />
        </div>
        {items.map((item: any, i: number) => {
          return <SearchItem key={item.data.id || i} type={item.type} data={item.data} />;
        })}
      </>
    )
  );
};

const SearchBar: React.FC<{ className?: string; onSelect?: CallableFunction; align?: any }> = ({ className, onSelect, align }) => {
  const { api } = useUser();
  const { i18n } = useLingui();

  const [search, setSearch] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchResult, setSearchResult] = useState<any | null>(null);
  const pathname = usePathname();

  useEffect(() => {
    setShowSearchResults(false);
    onSelect?.();
  }, [pathname, onSelect]);

  useEffect(() => {
    if (!search || search.length < 3) {
      setSearchResult(null);
      setShowSearchResults(false);
      return undefined;
    }

    const controller = new AbortController();
    setIsSearching(true);
    setShowSearchResults(true);
    const timeout = setTimeout(() => {
      api
        ?.search(search, { signal: controller.signal })
        .then((results) => {
          setSearchResult(results);
        })
        .finally(() => {
          setIsSearching(false);
        });
    }, 500);
    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  }, [search, api]);

  return (
    <div className={clsx(styles.root, className)}>
      <Dropdown
        className={styles.search}
        open={showSearchResults}
        align={align || 'left'}
        onOpen={() => {
          setShowSearchResults(!!search);
        }}
        onClose={() => {
          setShowSearchResults(false);
        }}
      >
        <Dropdown.Trigger>
          <SearchInput
            value={search}
            onChange={({ target }) => {
              setSearch(target.value);
            }}
            placeholder={i18n._(msg`Search users, collections or recommendations`)}
          />
        </Dropdown.Trigger>
        <Dropdown.Content className={clsx(styles.resultList)}>
          {isSearching && <Spinner className={styles.loading} />}

          {!isSearching && searchResult && hasResults(searchResult) && (
            <>
              <Section title={<span className="text-[#FACA2C]">{i18n._(msg`Top Result`)}</span>} items={searchResult.topResult} />
              <Section title={i18n._(msg`Products`)} items={searchResult.products} />
              <Section title={i18n._(msg`Collections`)} items={searchResult.collections} />
              <Section title={i18n._(msg`Users`)} items={searchResult.users} />
            </>
          )}
          {!isSearching && !hasResults(searchResult) && (
            <div className="m-3 text-center">
              <Trans>Oops! we were not able to find anything 😅</Trans>
            </div>
          )}
        </Dropdown.Content>
      </Dropdown>
    </div>
  );
};

export default SearchBar;
